import React from "react";
import { Agent, useCloneAgent, useDeleteAgent, useSetActiveAgent } from "@/lib/agents-store";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Edit3, Copy, Trash2, Rocket, MoreHorizontal } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
interface AgentCardProps {
  agent: Agent;
  onOpenTest: () => void;
  onEdit: (agent: Agent) => void;
}
export function AgentCard({ agent, onOpenTest, onEdit }: AgentCardProps) {
  const cloneAgent = useCloneAgent();
  const deleteAgent = useDeleteAgent();
  const setActiveAgent = useSetActiveAgent();
  const statusColors = {
    active: "bg-green-500",
    draft: "bg-slate-400",
    paused: "bg-amber-500",
    marketplace: "bg-chart-4"
  };
  const handleClone = () => {
    cloneAgent(agent.id);
    toast.success("Agent cloned successfully");
  };
  const handleDelete = () => {
    deleteAgent(agent.id);
    toast.error("Agent deleted");
  };
  const handleTest = () => {
    setActiveAgent(agent.id);
    onOpenTest();
  };
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card className="group relative overflow-hidden border-white/30 bg-card/60 backdrop-blur-xl hover:border-primary/40 hover:shadow-glow transition-all">
        <div className="aspect-video w-full overflow-hidden relative">
          <img
            src={agent.avatarUrl}
            alt={agent.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge className={`${statusColors[agent.status]} text-white border-0 capitalize`}>
              <div className="w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse" />
              {agent.status}
            </Badge>
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity gap-3 bg-black/40 backdrop-blur-[2px]">
            <Button size="icon" className="rounded-full bg-white text-black hover:bg-white/90" onClick={handleTest}>
              <Play className="w-4 h-4 fill-current" />
            </Button>
            <Button size="icon" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onEdit(agent)}>
              <Edit3 className="w-4 h-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="secondary" className="rounded-full">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleClone} className="gap-2">
                  <Copy className="w-4 h-4" /> Clone Agent
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(agent)} className="gap-2">
                  <Rocket className="w-4 h-4" /> Deploy
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDelete} className="text-destructive gap-2">
                  <Trash2 className="w-4 h-4" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-lg truncate">{agent.displayName}</h3>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{agent.role}</Badge>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed h-10">
              {agent.description}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/5">
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase font-semibold">Runs</p>
              <p className="text-sm font-bold">{agent.metrics.runs}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase font-semibold">Avg Cost</p>
              <p className="text-sm font-bold">${agent.metrics.avgCost}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground uppercase font-semibold">Success</p>
              <p className="text-sm font-bold text-primary">{agent.metrics.successRate}%</p>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
